import { CATALOG } from "../catalog";
import type { Category, ComponentDefinition } from "../types";
import type { AssistantPayload } from "./types";

// Broad requests and short follow-ups may name no equipment at all. Keep real
// examples of the main charging paths available as well as DC distribution;
// these are choices, not requirements or prevalidated system compatibility.
const BASELINE_EQUIPMENT_IDS = new Set(["solar-renogy-100", "mppt-victron-10030", "dcdc-victron-orion-30", "load-inverter-victron-phoenix-1200"]);
const ESSENTIAL_IDS = new Set(["dist-pos-bus", "dist-neg-bus", "dist-bs-5025", "load-generic", "batt-lifepo4-100", "batt-agm-100", ...BASELINE_EQUIPMENT_IDS]);
const STOP_WORDS = new Set("add another can could for from have into just layout make please select system that the their them then there these this tidy two use want with would".split(" "));
const WORD_ALIASES: Record<string, string> = {
  batteries: "battery", panels: "panel", chargers: "charger", controllers: "controller", fridges: "fridge", pumps: "pump", lights: "light", fuses: "fuse", busbars: "busbar", inverters: "inverter", monitors: "monitor", engines: "engine",
  batterie: "battery", batterien: "battery", batteri: "battery", batterier: "battery",
  solpanel: "panel", solpaneler: "panel", solarpanel: "panel", solarpanele: "panel", solarpaneele: "panel", solarmodul: "panel", solarmodule: "panel",
  kuhlschrank: "fridge", kuhlschranke: "fridge", kylskap: "fridge", refrigerator: "fridge",
  laddare: "charger", ladegerat: "charger", ladegerate: "charger",
};

function normalize(text: string): string {
  return text.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/(\d+(?:[.,]\d+)?)[\s-]*(watts?|volts?|amps?|ah|w|v|a)\b/g, (_all, value: string, unit: string) => `${value.replace(",", ".")}${unit.startsWith("w") ? "w" : unit.startsWith("v") ? "v" : unit === "ah" ? "ah" : "a"}`);
}
function tokens(text: string): Set<string> {
  return new Set((normalize(text).match(/[a-z0-9]+(?:\.[0-9]+[a-z]*)?/g) ?? []).map(token => WORD_ALIASES[token] ?? token).filter(token => token.length > 1 && !STOP_WORDS.has(token)));
}
function categories(words: Set<string>): Set<Category> {
  const wanted = new Set<Category>();
  const has = (...values: string[]) => values.some(value => words.has(value));
  if (has("battery", "lifepo4", "agm")) wanted.add("battery");
  if (has("charger", "controller", "mppt", "pwm", "dcdc")) wanted.add("charger");
  // "Solar controller" is a charger. "Solar panels and a controller" needs both.
  if (has("photovoltaic") || has("solar", "pv") && (has("panel") || !wanted.has("charger")) || has("panel") && !has("fuse", "circuit", "distribution", "switch")) wanted.add("solar");
  if (has("fridge", "pump", "light", "inverter", "heater", "fan", "load")) wanted.add("load");
  if (has("fuse", "busbar", "distribution", "isolator", "switch")) wanted.add("distribution");
  if (has("monitor", "shunt", "smartshunt")) wanted.add("monitor");
  if (has("engine", "motor")) wanted.add("engine");
  if (has("alternator", "generator", "inlet")) wanted.add("source");
  return wanted;
}

type Query = { text: string; words: Set<string>; categories: Set<Category> };
function query(text: string): Query {
  const words = tokens(text);
  return { text: normalize(text), words, categories: categories(words) };
}
function identityMatch(def: ComponentDefinition, text: string): boolean {
  // Model punctuation varies between user input and the catalog. Require both
  // letters and numbers so a bare model like "100" cannot match every rating.
  const compact = text.replace(/[^a-z0-9]/g, "");
  const model = normalize(def.model ?? "").replace(/[^a-z0-9]/g, "");
  return model.length >= 4 && /[a-z]/.test(model) && /\d/.test(model) && compact.includes(model)
    || (!!def.manufacturer || text.includes("generic")) && text.includes(normalize(def.name)) || text.includes(def.id);
}
function ratingScore(def: ComponentDefinition, words: Set<string>): number {
  let score = 0;
  for (const word of words) {
    const match = word.match(/^(\d+(?:\.\d+)?)(w|ah|v|a)$/);
    if (!match) continue;
    const requested = Number(match[1]);
    const values = match[2] === "w" ? [def.watts] : match[2] === "ah" ? [def.capacityAh]
      : match[2] === "v" ? [...(def.compatibleVoltages ?? []), def.voltage] : [def.ampOutput, def.ampDraw];
    if (values.includes(requested)) score += 40;
    // Nearby power/capacity options remain discoverable, with their real
    // ratings. This is retrieval only, never a declaration of compatibility.
    else if (match[2] === "w" || match[2] === "ah") {
      const value = values[0];
      if (value && requested > 0) score += 15 * Math.min(value, requested) / Math.max(value, requested);
    }
  }
  return score;
}

/** Current requests outrank conversation history; assistant prose is not a
 * search request. Keep each requested category, exact identities and declared
 * ratings discoverable, while retaining current drawing parts and plumbing. */
export function assistantCatalogSelection(payload: AssistantPayload): { catalog: ComponentDefinition[]; candidates: ComponentDefinition[] } {
  const current = query(payload.prompt);
  const previous = payload.history.filter(turn => turn.role === "user").slice(-2).reverse().map(turn => query(turn.content));
  const wanted = current.categories.size ? current.categories : previous.find(turn => turn.categories.size)?.categories ?? new Set<Category>();
  const scored = CATALOG.map((def, index) => {
    const words = tokens(`${def.id} ${def.name} ${def.shortName ?? ""} ${def.subtitle ?? ""} ${def.chemistry ?? ""} ${def.manufacturer ?? ""} ${def.model ?? ""} ${def.category}`);
    const lexical = (request: Query) => [...request.words].reduce((sum, token) => sum + (words.has(token) ? Math.min(token.length, 12) : 0), 0);
    const exact = identityMatch(def, current.text);
    const currentScore = lexical(current) + ratingScore(def, current.words);
    const historyScore = previous.reduce((sum, request, i) => sum + (lexical(request) + ratingScore(def, request.words) + (identityMatch(def, request.text) ? 40 : 0)) / (i + 1), 0);
    const relevance = (exact ? 2000 : 0) + (wanted.has(def.category) ? 400 : 0) + currentScore * 4 + historyScore;
    // Prefer a real catalog identity to a generic planning placeholder when
    // both satisfy the request. Explicit generic requests keep their choice.
    const identifiedPart = relevance > 0 && def.manufacturer && !current.words.has("generic") ? 30 : 0;
    return { def, index, score: relevance + identifiedPart };
  }).filter(item => item.score > 0).sort((a, b) => b.score - a.score || a.index - b.index);

  // A request can mention several equipment types. A populous category must
  // not consume every slot before the other requested types are represented.
  const selected = new Set<string>();
  for (const category of wanted) for (const item of scored.filter(item => item.def.category === category).slice(0, 3)) selected.add(item.def.id);
  for (const item of scored) {
    if (selected.size >= 28) break;
    selected.add(item.def.id);
  }
  const ranked = scored.filter(item => selected.has(item.def.id)).map(item => item.def);
  const existing = new Set(payload.system.nodes.filter(node => node.kind === "component").map(node => node.definitionId));
  return {
    candidates: [...ranked, ...CATALOG.filter(def => !selected.has(def.id) && BASELINE_EQUIPMENT_IDS.has(def.id))],
    catalog: [...ranked, ...CATALOG.filter(def => !selected.has(def.id) && (existing.has(def.id) || ESSENTIAL_IDS.has(def.id)))],
  };
}
